import React from "react";
import { motion } from "framer-motion";
import { GoDotFill } from "react-icons/go";
import { CiCircleCheck } from "react-icons/ci";

const experiences = [
    {
      role: "Senior Software Engineer",
      company: "Carrotsuite (ERP Solutions)",
      type: "Lagos / Remote",
      duration: "2022 – Present",
responsibilities: [
  "Own backend development on a multi-tenant ERP serving client businesses in production across seven industries — construction, procurement, HR/performance, healthcare (HMS), agriculture (farm & fish-farm), and logistics — on Node.js/Express + Sequelize (MySQL) with per-company subdomain tenancy.",
  "Mentor junior engineers and review merge requests in a GitLab workflow with CI/CD pipelines across staging and production.",
  "Designed and shipped enterprise features for client branding, scheduling, and document security — encrypted, watermarked PDF generation (pdf-lib, PDFKit, qpdf).",
  "Built and extended the multi-stage purchase-approval workflow from requisition to final authorization with complete per-stage audit logging.",
  "Diagnosed and fixed five production defects in the Construction module, including a milestone-progress query failure, a GL-vs-category mismatch corrupting expense reports, and an approval-summary endpoint returning one approver instead of the full chain.",
  "Integrated Paystack payments, multi-provider storage (Cloudinary, Backblaze B2, S3), email/SMS (Mailgun, SES, Twilio), Expo/Firebase push, Socket.IO real-time updates, and scheduled jobs (node-cron, AWS Scheduler).",
],
    },
    {
      role: "Senior Software Engineer (Contract)",
      company: "AFRIMA — All Africa Music Awards",
      type: "Remote",
      duration: "2025 – Present",
responsibilities: [
  "Led backend development of a continent-scale adjudication platform used by distributed jurors to evaluate 10,000+ artists across regional and multi-continental categories, replacing spreadsheet scoring; maintain the platform across award cycles.",
  "Architected a multi-stage evaluation pipeline with admin-gated approvals, duplicate-safe submissions, and full audit logging for scoring integrity across judges and categories.",
  "Engineered MongoDB aggregation pipelines computing weighted averages, rankings, and percentage scores; indexing and pipeline tuning cut query latency 40–60% at ≤1s p95 under concurrent load.",
  "Automated results collation and exports (ExcelJS, PDFKit), reducing manual reporting effort ~70–80% for organizers.",
],
    },
    {
      role: "Senior Backend Engineer (Contract)",
      company: "Hallos — Creators Marketplace & Livestreaming",
      type: "Remote",
      duration: "2024 – 2026",
responsibilities: [
  "Architected a multi-currency creators' marketplace enabling 1,000+ educators to sell live classes and courses, processing ₦50M+ across NGN/USD/GBP/EUR wallets with automated payment routing (Paystack/Stripe).",
  "Engineered a financially consistent wallet and transaction ledger with PostgreSQL row-level locking and ACID transactions — zero balance inconsistencies across 10K+ concurrent operations.",
  "Implemented multi-layer fraud prevention (2FA withdrawals, velocity checks, risk scoring, idempotency), blocking 98% of fraudulent attempts and eliminating duplicate charges on payment retries.",
  "Built livestreaming infrastructure (ZegoCloud + Mux) supporting 500+ concurrent participants per session at <2s latency, with automated lifecycle cleanup reducing server overhead ~40%.",
  "Optimized high-traffic financial and enrollment queries via indexing and tuning — API latency ~800ms → <200ms at 10K+ daily active users.",
  "Integrated AWS Bedrock AI pipelines for course transcripts and adaptive quizzes (peer-vs-peer tournaments, WAEC/JAMB prep); built audit-compliant financial logging and analytics across 50K+ transactions/events.",
],
    },
    {
      role: "Freelance & Contract Software Engineer",
      company: "Nigerian, UK & US Clients",
      type: "Remote",
      duration: "2020 – Present",
responsibilities: [
  "AreaFada OS (Creator-Economy SaaS): Took an AI-scaffolded prototype to production — migrated infrastructure to Render/Vercel with graceful shutdown and tuned pooling; authored a schema-integrity migration adding foreign keys and indexes across an 86-table PostgreSQL schema; found and fixed SSRF, stored XSS, an OAuth-credential-leaking endpoint, and a non-constant-time admin auth check; wrote the platform's first payment-webhook tests and converted racy reward counters to atomic SQL increments.",
  "Safiox (Emergency Response & Community Safety Platform): Full-stack development connecting people in danger with nearby responders — SOS alerts with live GPS, geospatial responder discovery (MongoDB 2dsphere/$nearSphere), simultaneous push + Socket.IO broadcast, per-alert live chat with location streaming, and a 40+ screen React Native (Expo) client.",
  "Yupacgo (AI-Powered Investment Platform): Architected a 54-endpoint REST API integrating three market-data providers with intelligent failover and Redis caching, improving response times 80%; implemented JWT + OTP auth, RBAC, and automated price-monitoring jobs.",
  "Gulf of Guinea Explorations Group (Oil & Gas): Delivered corporate web platforms and executive dashboards — Lighthouse 90+ mobile scores, accessible Chart.js dashboards at INP ≤200ms, and SEO work driving ~40% organic-traffic growth in two months.",
  "Additional engagements: DFX Limited (LMS, e-commerce, agency-lead platform), FinCalc (finance tracker grown to 1,000+ active users), pinkyshelbybeauty US e-commerce (~20% checkout uplift), SoleComfortShoes, and Lana Renovations.",
],
    }
  ];
     

const Experience = () => {
  return (
    <div className="container2" id="experience">
      <motion.div
        className="history py-5"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
      >
        <div className="sec-header mb-4 text-center ">
            <h2 className="text-bold">Experience</h2>
            <p className="about-text text-muted">My Professional Background</p>
        </div>
        <section className="timeline-section">
          <div className="timeline-items">
            {experiences.map((exp, index) => (
              <motion.div
                className="timeline-item"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-date ht">{exp.role}</div>
                <div className="work-h d-flex align-items-center gap-3">
                  <p className="fw-bolder se">{exp.company}</p>
                  <GoDotFill />
                  <p>{exp.type}</p>
                  <GoDotFill />
                  <p>{exp.duration}</p>
                </div>
                <div className="timeline-content mt-3 d-flex flex-column gap-3">
                  {exp.responsibilities.map((task, i) => (
                    <li className="d-flex gap-2" key={i}>
                      <CiCircleCheck className="w-icon" />
                      <small>{task}</small>
                    </li>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default Experience;
